import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { impalaAuthTypes } from 'app/model/linkedservice/impala.linkedservice';
import { enableSslWithTUAARadioSwitch, enableSslSimplified } from './linked-service-shared-fields';
import { format } from 'app/shared/utils/string.util';
import { ConnectionAuthKind } from 'shell-models-lib';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { GlobalVariables } from 'app/services/globals';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';

function connectionForm(version: string): ControlMetadata[] {
    return [
        {
            type: 'textbox',
            bind: 'host',
            label: stringResources.ModelName_Host,
            required: true,
            placeholder: stringResources.Host_Placeholder,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true
        },
        {
            type: 'textbox-number',
            bind: 'port',
            required: version !== driverVersions.v1,
            validators: version !== driverVersions.v1 ? [
                { type: 'required', errorMessage: '' }
            ] : [],
            label: stringResources.ModelName_Port,
            isLabelVertical: true,
            inputType: 'number',
            defaultValue: 21050,
            min: 0
        },
        {
            type: 'select-switch',
            bind: 'thriftTransportProtocol',
            label: stringResources.ModelName_ThriftTransportProtocol,
            defaultValue:   'Binary' ,
            hide: version === driverVersions.v1,
            isLabelVertical: true,
            options:  
            [
                {
                    label: stringResources.ModelName_BinaryDataset,
                    value: 'Binary',
                },
                {
                    label: stringResources.HttpServer_DisplayText,
                    value: 'HTTP'
                }
            ],         
        },
        {
            type: 'select-switch',
            bind: 'authenticationType',
            label: stringResources.ModelName_AuthenticationType,
            options: version === driverVersions.v2 ? 
            [
                {
                    label: stringResources.Anonymous_DisplayText,
                    value: impalaAuthTypes.Anonymous
                },
                {
                    label: stringResources.AuthenticationType_UsernameAndPassword,
                    value: impalaAuthTypes.UsernameAndPassword
                }
            ]
            :
            [
                {
                    label: stringResources.Anonymous_DisplayText,
                    value: impalaAuthTypes.Anonymous
                },
                {
                    label: stringResources.AuthenticationType_SASLUsername,
                    value: impalaAuthTypes.SASLUsername
                },
                {
                    label: stringResources.AuthenticationType_UsernameAndPassword,
                    value: impalaAuthTypes.UsernameAndPassword
                }
            ],
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true,
            defaultValue: impalaAuthTypes.Anonymous,
            cases: [
                {
                    caseValue: impalaAuthTypes.SASLUsername,
                    children: [
                        {
                            type: 'textbox',
                            bind: 'username',
                            label: stringResources.ModelName_UserName,
                            isLabelVertical: true
                        }
                    ]
                },
                {
                    caseValue: impalaAuthTypes.UsernameAndPassword,
                    children: [
                        {
                            type: 'textbox',
                            bind: 'username',
                            label: stringResources.ModelName_UserName,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        },
                        {
                            type: 'dynamic-akv-switcher',
                            bind: 'password',
                            label: stringResources.ModelName_Password,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_Password,
                                required: true,
                                isLabelVertical: true
                            },
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                stringResources.Impala_DisplayText,
                                stringResources.ModelName_Password)
                        }
                    ]
                }
            ]
        },
        version === driverVersions.v2 ? enableSslSimplified :enableSslWithTUAARadioSwitch
    ]
}
export const impalaMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.impala.linkedServiceType,
    displayName: stringResources.Impala_DisplayText,
    supportedInTrident: GlobalVariables.tridentBatch1ConnectorPhase2,
    supportAutoFillCopilotInput: GlobalVariables.tridentBatch1ConnectorPhase2,
    isKnownInPipelineContext: true,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: ResourceKind.Impala
    },
    supportedAuthKindsInTrident: [ConnectionAuthKind.Anonymous, ConnectionAuthKind.Basic],
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-apache_impala'
    },
    isDeprecated: GlobalVariables.showLegacy ? undefined : '{ version !== "v2" }',
    showWarningMessage: '{ version !== "v2" }',
    warningMessage: format(
        stringResources.Connector_Legacy_IsNotSupportedWarning_alt5,
        stringResources.VersionLabel.toLocaleLowerCase(),
    ),
    warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-impala',
    isFirstClass: true,
    category: ['Database'],
    supportDatasetV2: true,
    copySinkTypes: [],
    copySourceTypes: [adfModelTypeMap.impala.copySourceType],
    SHIRVersion: {
        v2: '5.55.0.0'
    },
    controls: 
    [        
        {
            type: 'radio-switch',
            bind: 'version',
            label: stringResources.VersionLabel,
            isLabelVertical: true,
            required: true,
            options: driverVersionVersionOptions,
            defaultValue: driverVersions.v2,
            hide: GlobalVariables.showLegacy ? false : '{version === "v2" }',
            cases: [
                {
                    caseValue: driverVersions.v1,
                    children: [
                        {
                            type: 'hint',
                            hintMode: 'warning',
                            description: format(
                                stringResources.Connector_Legacy_IsNotSupportedWarning_alt6,
                                stringResources.VersionLabel.toLocaleLowerCase()),
                            link: 'https://learn.microsoft.com/azure/data-factory/connector-impala'
                        },
                        ...connectionForm(driverVersions.v1)
                    ]
                },
                {
                    caseValue: driverVersions.v2,
                    children: [...connectionForm(driverVersions.v2)]
                }
            ]
        },
        ...(connectionForm(driverVersions.v2)
                .map((control) => ({
                    ...control,
                    hide: !GlobalVariables.showLegacy ? '{version !== "v2" }' : true
                }))
            )
    ]
};
